import { useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Mic } from 'lucide-react';
import VoiceJournalOverlay from './VoiceJournalOverlay';
import VoiceJournalConfirmModal from './VoiceJournalConfirmModal';

// Routes where the mic FAB is shown for coaches
const VOICE_ROUTES = [
  /^\/dressing-room$/,
  /^\/journal$/,
  /^\/match-notes$/,
  /^\/players$/,
  /^\/player\/[^/]+$/,
  /^\/match\/[^/]+\/edit$/,
];

function getRouteContext(pathname) {
  const playerMatch = pathname.match(/^\/player\/([^/]+)$/);
  if (playerMatch) return { context_player_id: playerMatch[1] };
  const matchMatch = pathname.match(/^\/match\/([^/]+)\/edit$/);
  if (matchMatch) return { context_match_id: matchMatch[1] };
  return {};
}

export default function VoiceJournalFAB() {
  const { user, isAdmin } = useAuth();
  const location = useLocation();
  const [recording, setRecording] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [confirmPayload, setConfirmPayload] = useState(null);
  const longPressTimer = useRef(null);

  if (!user || !isAdmin) return null;

  const visible = VOICE_ROUTES.some(re => re.test(location.pathname));
  if (!visible) return null;

  const context = getRouteContext(location.pathname);

  const handlePointerDown = (e) => {
    e.preventDefault();
    longPressTimer.current = setTimeout(() => {
      setRecording(true);
      setShowOverlay(true);
    }, 350);
  };

  const handlePointerUp = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    // If recording started, the overlay handles stop on its own pointer-up
  };

  const handleRecordingComplete = async (blob) => {
    setRecording(false);
    setShowOverlay(false);
    setAnalysing(true);
    try {
      const axios = (await import('axios')).default;
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const fd = new FormData();
      fd.append('audio', blob, 'voice.webm');
      fd.append('language', 'en');
      const tx = await axios.post(`${API}/voice-journal/transcribe`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const transcript = (tx.data?.transcript || '').trim();
      if (!transcript) {
        const { toast } = await import('sonner');
        toast.error("Couldn't hear anything — try again?");
        return;
      }
      const an = await axios.post(`${API}/voice-journal/analyse`, {
        transcript,
        ...context,
      });
      setConfirmPayload(an.data);
    } catch (err) {
      const { toast } = await import('sonner');
      toast.error(err.response?.data?.detail || 'Voice processing failed');
    } finally {
      setAnalysing(false);
    }
  };

  const handleRecordingCancelled = () => {
    setRecording(false);
    setShowOverlay(false);
  };

  return (
    <>
      <button
        type="button"
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        onContextMenu={(e) => e.preventDefault()}
        className={`fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-105 active:scale-95 select-none touch-none ${
          analysing
            ? 'bg-amber-500 text-white animate-pulse'
            : 'bg-emerald-600 hover:bg-emerald-700 text-white'
        }`}
        title="Long-press to record a voice note"
        aria-label="Record voice note"
        data-testid="voice-journal-fab"
      >
        <Mic className="w-6 h-6" strokeWidth={1.75} />
      </button>

      {showOverlay && (
        <VoiceJournalOverlay
          onComplete={handleRecordingComplete}
          onCancel={handleRecordingCancelled}
        />
      )}

      {confirmPayload && (
        <VoiceJournalConfirmModal
          payload={confirmPayload}
          context={context}
          onClose={() => setConfirmPayload(null)}
        />
      )}
    </>
  );
}
